import type { ChartUnit } from '../../core/types/chart-unit.js';
import type { HistogramSeries } from '../../histogram/types/histogram.js';
import type { XYChartData } from '../../xy-chart/types/xy-chart.js';

export interface SeriesColor {
  name: string;
  color: string;
}
interface TransformHistogramDataResult {
  barSeriesProps: BarSeriesProps[];
  units: Set<ChartUnit>;
  seriesColors: SeriesColor[];
}

type BarSeriesProps = {
  data: XYChartData[];
  unit: ChartUnit;
  seriesCount: number;
};

export function transformHistogramDataToXYChart(
  data: HistogramSeries[],
): TransformHistogramDataResult {
  const seriesColors: SeriesColor[] = [];
  const units: Set<ChartUnit> = new Set();
  const unitToData = new Map<ChartUnit, XYChartData[]>();
  const unitToSeriesCount = new Map<ChartUnit, number>();

  data.forEach((series, index) => {
    const { bins, name, color, unit, ...seriesUserData } = series;
    const hasUserData = Object.keys(seriesUserData).length > 0;

    if (color) {
      seriesColors.push({ name, color });
    }

    let bucket = unitToData.get(unit);
    if (bucket === undefined) {
      bucket = [];
      unitToData.set(unit, bucket);
    }

    const seriesId = index.toString();
    for (let i = 0; i < bins.length; i++) {
      const bin = bins[i];
      if (hasUserData) {
        bucket.push({
          ...seriesUserData,
          x0: bin.from,
          x1: bin.to,
          y1: bin.value,
          name,
          seriesId,
        });
      } else {
        bucket.push({
          x0: bin.from,
          x1: bin.to,
          y1: bin.value,
          name,
          seriesId,
        });
      }
    }

    unitToSeriesCount.set(unit, (unitToSeriesCount.get(unit) ?? 0) + 1);
    units.add(unit);
  });

  return {
    barSeriesProps: Array.from(unitToData, ([unit, data]) => ({
      unit,
      data,
      seriesCount: unitToSeriesCount.get(unit) ?? 1,
    })),
    units,
    seriesColors,
  };
}
